<template>
  <div v-if="loading">
    
  </div>
  <div v-else>

    <div v-if="report">

      <v-btn
        outlined
        class="text-none mb-4 pl-3 pr-3"
        color="gray12Color"
        style="background: var(--v-whiteColor-base);"
        @click="$router.push(
          {
            path: `/sites/${site.id}/campaign/${campaign.id}/keyword-insight`,
            ...isContentOnlyView
              ? {
                query: { fromQuickContentOrdersTab: $route.query.fromQuickContentOrdersTab },
              }
              : {},
          })"
      >
        <i class="material-icons-outlined mr-1" style="color: var(--v-gray1Color-base); font-size: 1rem;">arrow_back</i> <span style="color: var(--v-blackColor-base);">Back to Keyword Insight</span>
      </v-btn>

      <div class="mb-8">
        <div class="mb-1" style="color: var(--v-gray50Color-base); font-size: 1rem;">{{ site.name }}</div>
        
        <div class="d-flex align-center justify-space-between gap10 mb-3">
          <div class="card-big-title" style="color: var(--v-blackColor-base); font-weight: 700; font-size: 1.5rem;">
            Keyword Insight results
          </div>

          <div class="d-flex align-center gap10">
            <v-btn
              outlined
              class="text-none"
              color="gray12Color"
              style="background: var(--v-whiteColor-base);"
              href="https://help.pageoptimizer.pro/article/difference-between-top-level-and-supporting-content-keywords"
              target="_blank"
            >
              <i class="material-icons mr-1" style="color: var(--v-gray1Color-base); font-size: 1rem;">school</i> <span style="color: var(--v-blackColor-base);">How to use this dashboard?</span>
            </v-btn>
          </div>
        </div>
      </div>
    
      
      <div
        v-if="currentAsyncTask?.is_pending_or_in_progress"
        class="d-flex flex-column align-center pt-8 pb-8"
      >
        <div class="d-flex flex-column align-center" style="max-width: 500px; text-align: center;">
          <img src="/static/keyword-insight-processing-image.svg" width="315" height="222" class="mb-4" />
          <div class="mb-6" style="font-size: 1.2rem; font-weight: bold;">Getting Keyword Insight data...</div>
    
          <v-progress-linear
            class="mb-4" 
            style="max-width: 300px;"
            rounded height="5"
            color="profileAnchorColor"
            :value="currentAsyncTask.task_status.value || 0">
          </v-progress-linear>

          <div style="font-size: 1rem; color: var(--v-gray50Color-base);">{{ currentAsyncTask.task_status.status === 'PENDING' ? 'Starting...' : currentAsyncTask.task_status.msg }}</div>
        </div>  
      </div>

      <keyword-research-result
        v-else
        title="Keyword Insight"
        reportType="KeywordResearch"
        :report="report"
        :researchHistory="[]"
        :hasUnlimitedAccess="hasUnlimitedAccess"
        :isContentOnlyView="isContentOnlyView"

        @refreshReport="() => loadReport()"
        @onAIResultReady="() => loadReport()"
        @onKeywordLabelUpdateSuccess="onKeywordLabelUpdateSuccess"
      />

    </div>

    <div v-else>
      <div class="card-big-title mb-8 d-flex gap10">
        No report found
      </div>
    </div>

  </div>
</template>

<script>
import KeywordResearchResult from '@/components/Campaigns/KeywordResearchResult.vue';
import rockyKeywordScreenMixin, {
  KEYWORD_SCREENS,
  short as rockyShort,
} from '../../rockyKeywordScreen.js';


export default {
  mixins: [rockyKeywordScreenMixin],
  props: ['site', 'campaign', 'hasUnlimitedAccess', 'isWhiteGloveUser', 'isMissionControlSession', 'reloadUser'],
  components: {
    KeywordResearchResult
  },
  data () {
    return {
      loading: true,
      report: null,
    }
  },
  computed: {
    isContentOnlyView () {
      return Boolean(this.$route.query.fromQuickContentOrdersTab)
    },
    asyncTasks() {
      return this.$store.state.asyncTasks
    },
    currentAsyncTask() {
      return this.asyncTasks.filter(task => task.type.startsWith('KeywordResearch.') && task.record_id === this.report?.id)[0]
    },
  },
  watch: {
    // Rocky's refresh signal (PopAssistantButton.handleRefresh). A research
    // POP Pilot started through the API is not in the store's task list, so
    // currentAsyncTask never flips here; reload the report when asked.
    '$store.state.triggerRockyRefresh': function () {
      this.loadReport()
    },
    currentAsyncTask: function (newVal, oldVal) {
      if (newVal?.is_completed && oldVal && newVal?.is_completed !== oldVal?.is_completed) {
        this.reloadUser()
        this.loadReport()
      }
      this.emitRockyKeywordScreenDebounced()
    }
  },
  methods: {
    // Rocky snapshot for the states the results table doesn't cover: loading,
    // generating and missing report. When the KeywordResearchResult child is
    // rendered, it emits the ready snapshot itself — return null here.
    buildRockyScreenData () {
      if (this.$route.name !== 'keyword-insight-single') return null
      const generating = !!this.currentAsyncTask?.is_pending_or_in_progress
      if (!this.loading && this.report && !generating) return null
      let status = 'loading'
      if (!this.loading) {
        status = generating ? 'generating' : (this.report ? 'loading' : 'empty')
      }
      return {
        screen: KEYWORD_SCREENS.KEYWORD_INSIGHT_RESULTS,
        status,
        loading: this.loading || generating,
        recordIds: { keywordResearchId: this.$route.params.id },
        data: {
          progressPct: this.currentAsyncTask?.task_status?.value ?? null,
          progressMsg: rockyShort(this.currentAsyncTask?.task_status?.msg),
        },
      }
    },
    loadReport () {
      return this.$store.dispatch('loadKeywordResearchData', {
        researchId: this.$route.params.id,
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.report = response.data.research
        }
      })
    },
    onKeywordLabelUpdateSuccess(keywords, label, resetExistingTopLevelKeywords) {
      this.report = {
        ...this.report,
        result: this.report.result.map((row) => {
          if (label === 'T' && row.isTopLevelKeyword && resetExistingTopLevelKeywords) {
            return {
              ...row,
              isTopLevelKeyword: false,
              isSecondaryKeyword: false
            }
          }
          if (keywords.includes(row.keyword)) {
            return {
              ...row,
              isTopLevelKeyword: label === 'T' || label === 'ST',
              isSecondaryKeyword: label === 'ST',
              isSupportingKeyword: label === 'S',
            }
          }
          return row
        })
      }
    },
  },
  mounted () {
    this.emitRockyKeywordScreen()
    this.loadReport()
    .finally(() => {
      this.loading = false
      this.emitRockyKeywordScreen()
    })
  }
}
</script>

<style lang="scss" scoped>
@import "./../styles.scss";
</style>